import { Suspense, lazy } from 'react';

/**
 * The workflow's diagram, beside its board (spec 094 FR-010).
 *
 * **The whole point of this file is the boundary.** The workflow graph is React
 * Flow, which constitution Principle 6 names as a heavy surface that must sit
 * behind its own lazy boundary and must not load on routes that do not use it.
 * Spec 094 made the board such a route — but only when the reader asks, and by
 * FR-011 they have not asked by default.
 *
 * So this component is mounted only when the diagram is showing. Importing
 * `WorkflowGraphPage` directly in `BoardPage` would compile perfectly and cost
 * every board reader a graph they never open: the board's chunk is itself lazily
 * loaded, so the bundle check's "absent from the entry chunk" assertion would
 * still pass. `scripts/check-bundle.sh` was extended for exactly that case — it
 * now asserts the graph is absent from the board's own chunk, identified as the
 * one carrying dnd-kit.
 *
 * Reached through `features/workflows`' public interface rather than by deep
 * import: the board is a task surface that wants to show a workflow's structure,
 * which is a cross-feature call and goes through the front door (constitution §2).
 *
 * It asks for `WorkflowDiagram`, not `WorkflowGraphPage`. The page carries the
 * workflow's name, description and its own Edit link — all three already on screen
 * here — and its `flex-1` root collapses to nothing inside a fixed-height box.
 */
const WorkflowDiagram = lazy(() =>
  import('@/features/workflows').then((module) => ({
    default: module.WorkflowDiagram,
  })),
);

export interface BoardDiagramLazyProps {
  workspaceId: string;
  workflowKey: string;
}

export function BoardDiagramLazy({
  workspaceId,
  workflowKey,
}: BoardDiagramLazyProps) {
  return (
    <Suspense
      fallback={
        // A plain line rather than a skeleton of the graph: the reader asked for
        // the diagram a moment ago and is watching this space, so saying what is
        // happening beats drawing a shape that is about to be replaced.
        <p className="text-muted-foreground p-4 text-sm">
          Loading the diagram…
        </p>
      }
    >
      <WorkflowDiagram workspaceId={workspaceId} workflowKey={workflowKey} />
    </Suspense>
  );
}
